import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography,
} from "@mui/material";
import { createAhaProduct, setAhaProductModules, updateAhaProduct } from "../../api/apiClient";
import AhaIdentifiersDialog from "../../components/aha/AhaIdentifiersDialog";
import {
  GROUP_PATTERN, KEY_PATTERN, MIN_REASON, PRODUCT_NUMBER_PATTERN, serverMessage, suggestKey,
} from "./catalogFields";

const linksFrom = (product) => Object.fromEntries((product?.links || []).map((l) => [l.moduleKey, { included: true, group: l.exclusiveGroup || "" }]));

/** Create (mode="create") or edit (mode="edit") one catalog product. The key is fixed once created. */
export default function AhaProductDialog({ open, mode, product, modules, environment, onSaved, onChanged, onClose }) {
  const editing = mode === "edit";
  const [name, setName] = useState("");
  const [edition, setEdition] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [number, setNumber] = useState("");
  const [requiresSubcourse, setRequiresSubcourse] = useState(false);
  const [active, setActive] = useState(true);
  const [links, setLinks] = useState({});
  const [reason, setReason] = useState("");
  const [confirmInUse, setConfirmInUse] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [identifiersOpen, setIdentifiersOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing ? product.name : "");
    setEdition(editing ? product.edition || "" : "");
    setKey(editing ? product.key : "");
    setKeyTouched(false);
    setNumber("");
    setRequiresSubcourse(editing ? product.requiresSubcourse : false);
    setActive(editing ? product.active : true);
    setLinks(editing ? linksFrom(product) : {});
    setReason("");
    setConfirmInUse(false);
    setError("");
    setBusy(false);
    // Reset only when the dialog opens or targets another product: a reload of the same product (after a partial save
    // or an identifiers change) must not wipe unsaved edits or the error message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, product?.key]);

  const initialLinks = useMemo(() => linksFrom(editing ? product : null), [editing, product]);
  const shownModules = (modules || []).filter((m) => m.active || initialLinks[m.key]);
  const moduleOrder = (modules || []).map((m) => m.key);
  const rank = (moduleKey) => { const i = moduleOrder.indexOf(moduleKey); return i < 0 ? moduleOrder.length : i; };
  const byRank = (a, b) => rank(a) - rank(b) || a.localeCompare(b);
  // Built from the link state itself, not from the modules list, so a link to a module that list lacks is never dropped.
  const chosen = Object.keys(links).filter((k) => links[k].included).sort(byRank)
    .map((k) => ({ moduleKey: k, exclusiveGroup: links[k].group.trim() || null }));
  const initialChosen = Object.keys(initialLinks).sort(byRank)
    .map((k) => ({ moduleKey: k, exclusiveGroup: initialLinks[k].group || null }));

  const linksChanged = JSON.stringify(chosen) !== JSON.stringify(initialChosen);
  const productChanged = editing && (name.trim() !== product.name || edition.trim() !== (product.edition || "")
    || requiresSubcourse !== product.requiresSubcourse || active !== product.active);
  const deactivatingInUse = editing && product.active && !active && product.inUse > 0;
  const groupsValid = chosen.every((l) => !l.exclusiveGroup || GROUP_PATTERN.test(l.exclusiveGroup));
  const valid = name.trim().length > 0 && groupsValid && (editing
    ? (productChanged || linksChanged) && reason.trim().length >= MIN_REASON && (!deactivatingInUse || confirmInUse)
    : KEY_PATTERN.test(key) && PRODUCT_NUMBER_PATTERN.test(number.trim()));

  const changeName = (value) => {
    setName(value);
    if (!editing && !keyTouched) setKey(suggestKey(value, edition));
  };
  const changeEdition = (value) => {
    setEdition(value);
    if (!editing && !keyTouched) setKey(suggestKey(name, value));
  };
  const setLink = (moduleKey, patch) => setLinks((prev) => ({ ...prev, [moduleKey]: { included: false, group: "", ...prev[moduleKey], ...patch } }));

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    let savedProduct = false;
    try {
      if (editing) {
        if (productChanged) {
          await updateAhaProduct(product.key, {
            name: name.trim(), edition: edition.trim(), requiresSubcourse, active, reason: reason.trim(), confirmInUse,
          });
          savedProduct = true;
        }
        if (linksChanged) await setAhaProductModules(product.key, { links: chosen, reason: reason.trim() });
      } else {
        await createAhaProduct({
          key, name: name.trim(), edition: edition.trim(), productNumber: number.trim(), requiresSubcourse, links: chosen,
        });
      }
      onSaved();
    } catch (e) {
      const message = serverMessage(e, "The product could not be saved.");
      setError(savedProduct ? `The product details were saved, but the module change was not: ${message}` : message);
      if (savedProduct && onChanged) onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="aha-product-title">
        <DialogTitle id="aha-product-title">{editing ? `Edit ${product.name}` : "Add AHA product"}</DialogTitle>
        <DialogContent>
          <Alert severity="info" role="note" sx={{ mb: 2 }}>
            Shared by every training center. {editing ? "Changes apply to " : "It is created for "}AHA {environment} only, unverified and inactive
            until you verify and activate it.
          </Alert>
          <Box sx={{ display: "grid", gap: 2 }}>
            <TextField label="Product name" size="small" value={name} onChange={(e) => changeName(e.target.value)} />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 2 }}>
              <TextField label="Edition" size="small" value={edition} onChange={(e) => changeEdition(e.target.value)} placeholder="2025" />
              <TextField label="Product key" size="small" value={key} disabled={editing}
                onChange={(e) => { setKey(e.target.value.toUpperCase()); setKeyTouched(true); }}
                error={!editing && key.length > 0 && !KEY_PATTERN.test(key)}
                helperText={editing ? "The key cannot change once created." : "Capital letters, digits and underscores. Locked after creation."} />
            </Box>
            {editing ? (
              <Box>
                <Typography variant="body2">AHA product number in {environment}: <b>{product.environment?.productNumber || "none"}</b></Typography>
                <Button size="small" sx={{ mt: 0.5 }} onClick={() => setIdentifiersOpen(true)}>Change number or module IDs</Button>
              </Box>
            ) : (
              <TextField label="AHA product number" size="small" value={number} onChange={(e) => setNumber(e.target.value)}
                error={number.length > 0 && !PRODUCT_NUMBER_PATTERN.test(number.trim())} helperText="For example 25-3002" />
            )}
            <Box>
              <FormControlLabel label="Requires a subcourse" disabled={editing && product.inUse > 0}
                control={<Checkbox checked={requiresSubcourse} onChange={(e) => setRequiresSubcourse(e.target.checked)} />} />
              {editing && product.inUse > 0 && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {product.inUse} class setting(s) or template(s) use this product, so this cannot change.
                </Typography>
              )}
            </Box>
            {editing && (
              <Box>
                <FormControlLabel label="Active" control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />} />
                {deactivatingInUse && (
                  <FormControlLabel
                    label={`I understand ${product.inUse} class setting(s) or template(s) will stop sending AHA cards until they choose another product`}
                    control={<Checkbox checked={confirmInUse} onChange={(e) => setConfirmInUse(e.target.checked)} />} />
                )}
              </Box>
            )}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Modules</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Modules sharing an exclusive group are "choose one" alternatives.
              </Typography>
              {shownModules.length === 0 && <Typography variant="body2">No modules exist yet. Add them on the Modules tab.</Typography>}
              {shownModules.map((m) => {
                const link = links[m.key];
                return (
                  <Box key={m.key} sx={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 1, alignItems: "center" }}>
                    <FormControlLabel label={`${m.name}${m.active ? "" : " (inactive)"}`}
                      control={<Checkbox inputProps={{ "aria-label": `Include ${m.name}` }} checked={!!link?.included}
                        onChange={(e) => setLink(m.key, { included: e.target.checked })} />} />
                    <TextField size="small" placeholder="Exclusive group" disabled={!link?.included} value={link?.group || ""}
                      inputProps={{ "aria-label": `Exclusive group for ${m.name}` }}
                      onChange={(e) => setLink(m.key, { group: e.target.value })} />
                  </Box>
                );
              })}
            </Box>
            {editing && (
              <TextField label="Reason for change" size="small" value={reason} onChange={(e) => setReason(e.target.value)}
                helperText={`Recorded in the audit log (at least ${MIN_REASON} characters).`} multiline minRows={2} />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={submit} disabled={!valid || busy}>
            {editing ? "Save changes" : "Create product"}
          </Button>
        </DialogActions>
      </Dialog>
      {editing && (
        <AhaIdentifiersDialog open={identifiersOpen} onClose={() => setIdentifiersOpen(false)} productKey={product.key} onChanged={onChanged} />
      )}
    </>
  );
}
